import Helper from '@/utils/helper';
import Time from '@/utils/time';
import storage from '@/utils/storage';
import * as Enums from '@/utils/enums';
import type { FormErrors } from '@mantine/form';
import { FormItem, FormDefinitionField, FormTableDesign, FormTableValue } from '@/interfaces/api/models';

export type FormFieldValue = string | boolean | string[] | Date | number | null;

// `hidden` reflects the signature's `SignatureTemplate.HideSignature` setting — some templates
// are configured to never display the captured signature image back to whoever's filling out the
// form (e.g. a customer shouldn't see a technician's signature). Consumers must render a
// placeholder instead of `url` when this is true, never fall back to showing the image anyway.
export type SignaturePreview = { url: string; signedBy: string; signedDate: string; hidden: boolean };

// Shared prop shape consumed by every `PageComponents/Form/Fields/*` component
// (Task 9) and `FormFieldRenderer` (which additionally needs `field`). Individual
// field components narrow `value`/`onChange` to their own concrete type rather than
// redefining `label`/`required`/`onBlur`/`error`/`disabled` from scratch.
export interface FieldProps {
    label?: string;
    required?: boolean;
    value: FormFieldValue;
    onChange: (value: FormFieldValue) => void;
    onBlur?: () => void;
    error?: string;
    disabled?: boolean;
    // Set by `FormFieldTable` for every cell it renders through `FormFieldRenderer`. The app-wide
    // theme (theme/mantineTheme.ts) caps every input's root at maxWidth: 500 so fields never
    // stretch absurdly wide on a normal form — but inside a table cell that same cap stops the
    // field from ever filling its (often much wider) column. Field components read this to pass
    // `maw="100%"` to their underlying Mantine control, overriding the theme's cap for that one
    // instance without touching it anywhere else.
    inTable?: boolean;
}

export function decodeFormItemValue(dataResult: string | null | undefined, dataType: string): FormFieldValue {
    switch (dataType) {
        case 'Number':
            return dataResult === null || dataResult === undefined || dataResult === '' ? null : parseFloat(dataResult);
        case 'Checkbox':
            return Helper.stringToBool(dataResult);
        case 'Date':
        case 'DateTime': {
            // A malformed/unparseable stored value (e.g. stray text typed into a Date-typed table
            // cell) must never reach `ScDateControl` as a Date object: Mantine's `DateInput` formats
            // it internally via the native `Date.prototype.toISOString()`, which throws
            // "RangeError: Invalid time value" and crashes the whole tree with no error boundary to
            // catch it. `new Date(dataResult)` alone doesn't guard against this - an Invalid Date is
            // still a truthy object. See the equivalent guard in `sc-datepicker.tsx`'s `parseValidDate`.
            if (!dataResult) return null;
            const parsed = Time.parseDate(dataResult);
            return Time.isValidDate(parsed) ? parsed : null;
        }
        case 'MultiSelect':
            return dataResult ? Helper.deserializeCustomCSV(dataResult) : [];
        case 'Boolean':
        case 'String':
        case 'LongText':
        case 'Select':
        case 'Table':
        case 'Image':
        case 'Signature':
        case 'InformationalText':
        case 'InformationalImage':
        default:
            return dataResult ?? '';
    }
}

export function encodeFormItemValue(value: FormFieldValue, dataType: string): string | null {
    switch (dataType) {
        case 'Number':
            return value === null || value === undefined ? null : String(value);
        case 'Checkbox':
            return value ? 'true' : 'false';
        case 'Date':
        case 'DateTime':
            return value instanceof Date ? Time.toISOString(value, false, true) : (value as string | null) ?? null;
        case 'MultiSelect': {
            const arr = Array.isArray(value) ? value : [];
            return arr.length > 0 ? Helper.serializeCustomCSV(arr) : '';
        }
        default:
            return (value as string | null) ?? null;
    }
}

export function sameFieldValue(a: FormFieldValue, b: FormFieldValue): boolean {
    if (a instanceof Date || b instanceof Date) {
        return a instanceof Date && b instanceof Date && a.getTime() === b.getTime();
    }
    if (Array.isArray(a) || Array.isArray(b)) {
        return Array.isArray(a) && Array.isArray(b) && a.length === b.length && a.every((x, i) => x === b[i]);
    }
    return (a ?? null) === (b ?? null);
}

export function sameValues(a: Record<string, FormFieldValue>, b: Record<string, FormFieldValue>): boolean {
    const keys = Object.keys(a);
    if (keys.length !== Object.keys(b).length) return false;
    return keys.every((key) => sameFieldValue(a[key], b[key]));
}

export function parseJsonSafely<T>(raw: string | null | undefined, fallback: T): T {
    if (!raw) return fallback;
    try {
        return JSON.parse(raw) as T;
    } catch {
        return fallback;
    }
}

export function defaultDataResultForType(dataType?: string): string | null {
    switch (dataType) {
        case 'Number':
            return null;
        case 'Checkbox':
            return 'false';
        default:
            return '';
    }
}

export function getBlankFormItemFromField(field: FormDefinitionField, defaultSectionGroup = 0): FormItem {
    const now = Time.toISOString(Time.now());
    return {
        ID: Helper.newGuid(),
        FormDefinitionFieldID: field.ID,
        DisplayOrder: field.DisplayOrder,
        Section: field.Section,
        Description: field.Description,
        SectionGroup: field.Section && field.Section.Repeatable === true ? defaultSectionGroup : null,
        DataType: field.DataType,
        Required: field.Required,
        SectionID: field.SectionID,
        SectionHeading: field.Section ? field.Section.Heading : null,
        SectionDescription: field.Section ? field.Section.Description : null,
        SectionRepeatable: field.Section ? field.Section.Repeatable : null,
        SectionDisplayOrder: field.Section ? field.Section.DisplayOrder : null,
        ParentSectionID: field.Section ? field.Section.ParentSectionID : null,
        ParentSectionGroup: 0,
        CreatedDate: now,
        ModifiedDate: now,
        CreatedBy: storage.getCookie(Enums.Cookie.servUserName),
        ModifiedBy: storage.getCookie(Enums.Cookie.servUserName),
        DataResult: defaultDataResultForType(field.DataType),
        IsActive: true,
    };
}

export function getBlankFormItemFromOtherItem(originalItem: FormItem, defaultSectionGroup = 0, defaultParentSectionGroup = 0): FormItem {
    return {
        ID: Helper.newGuid(),
        FormDefinitionFieldID: originalItem.FormDefinitionFieldID,
        DisplayOrder: originalItem.DisplayOrder,
        Section: originalItem.Section,
        Description: originalItem.Description,
        SectionGroup: originalItem.SectionRepeatable === true ? defaultSectionGroup : null,
        DataType: originalItem.DataType,
        Required: originalItem.Required,
        SectionID: originalItem.SectionID,
        SectionHeading: originalItem.SectionHeading,
        SectionDescription: originalItem.SectionDescription,
        SectionRepeatable: originalItem.SectionRepeatable,
        SectionDisplayOrder: originalItem.SectionDisplayOrder,
        ParentSectionID: originalItem.ParentSectionID,
        ParentSectionGroup: defaultParentSectionGroup,
        DataResult: defaultDataResultForType(originalItem.DataType),
        IsActive: true,
    };
}

export function getTableCellID(columnName: string, rowName: string, rowIndex: string | number, sectionGroup = 0): string {
    return `${columnName}_${rowName}_${rowIndex}_${sectionGroup}`;
}

/**
 * Smooth-scrolls to the first invalid control after a failed `form.validate()`.
 *
 * "First" is taken from `Object.keys(errors)` rather than DOM order: `buildValidator` walks
 * `formItemsMeta` — which mirrors the order the form's own sections/items render in — inserting
 * each error as it reaches that item (and, for a `Table`, its cells before the table's own
 * required check), so the map's insertion order already tracks visual order closely enough to
 * call it "the first" without a separate DOM query.
 *
 * The element is found via `[data-form-item-id]`, which `FormFieldBinding` sets to the owning
 * `FormItem.ID` and `FormFieldTable` sets to the per-cell ID (`getTableCellID`) — the same two
 * key shapes `buildValidator` produces — so every error key this can receive resolves to some
 * element.
 */
export function scrollToFirstError(errors: FormErrors): void {
    const firstErrorKey = Object.keys(errors)[0];
    if (!firstErrorKey) return;

    const element = document.querySelector(`[data-form-item-id="${CSS.escape(firstErrorKey)}"]`);
    element?.scrollIntoView({ behavior: 'smooth', block: 'center' });
}

export interface SelectOptionItem {
    value: string;
    label: string;
}

// Real form definitions do contain duplicated (and blank) option labels, and the legacy
// Kendo-based dropdowns rendered every one of them as its own separate, independently
// selectable entry. Mantine's `Select`/`MultiSelect` `data`, however, uses each item's `value`
// as its React key *and* as the actual selected value — passing the same label twice collapses
// them to one visible entry and throws a duplicate-key warning. So each occurrence of a label
// gets its own unique `value` (only suffixed from the second occurrence onward, so the common,
// non-duplicated case round-trips as a plain, unsuffixed label) while `label` stays the same
// for display. Used only inside `Fields/FormFieldSelect.tsx`/`FormFieldMultiSelect.tsx` — the
// rest of the form (decode/encode, validation, autosave) keeps working on plain label strings.
const SELECT_OPTION_VALUE_SEPARATOR = '\u0000';

export function buildSelectOptionItems(options: string[]): SelectOptionItem[] {
    const occurrences: Record<string, number> = {};
    return options.map((label) => {
        const index = occurrences[label] ?? 0;
        occurrences[label] = index + 1;
        return { value: index === 0 ? label : `${label}${SELECT_OPTION_VALUE_SEPARATOR}${index}`, label };
    });
}

export function stripSelectOptionValueSuffix(value: string): string {
    const separatorIndex = value.indexOf(SELECT_OPTION_VALUE_SEPARATOR);
    return separatorIndex === -1 ? value : value.slice(0, separatorIndex);
}

/**
 * A `FormItem`'s identity *independent of its own `ID`*.
 *
 * A never-saved form's items are generated client-side by `getBlankFormItemFromField`, which
 * assigns a fresh `Helper.newGuid()` on every mount — so an item's `ID` is stable only within one
 * page load. That makes it useless as a key for anything that has to survive a reload, which is
 * exactly what the crash-recovery draft has to do. `FormDefinitionFieldID` says *which field* and
 * the two section-group numbers say *which repeat instance*, and together they are unique across
 * the item set (the item set is built one item per field per instance) and reproduced identically
 * on the next mount. Autosave snapshots key their values by this, not by `ID`.
 */
export function buildAutosaveComposite(item: FormItem): string {
    return `${item.FormDefinitionFieldID}:${item.SectionGroup ?? 0}:${item.ParentSectionGroup ?? 0}`;
}

export function parseAutosaveComposite(composite: string): { formDefinitionFieldID: string; sectionGroup: number; parentSectionGroup: number } | null {
    const parts = composite.split(':');
    if (parts.length !== 3) return null;
    const sectionGroup = Number(parts[1]);
    const parentSectionGroup = Number(parts[2]);
    if (!parts[0] || Number.isNaN(sectionGroup) || Number.isNaN(parentSectionGroup)) return null;
    return { formDefinitionFieldID: parts[0], sectionGroup, parentSectionGroup };
}

export interface SectionGroupNode {
    sectionID: string | null
    parentSectionID: string | null
    heading: string | null | undefined
    parentSectionGroup: number
    formItems: FormItem[]
    childSections: SectionGroupNode[]
    isRepeatable: boolean | null | undefined
    displayOrder: number | null | undefined
    sectionGroup: number
    isDeletable: boolean
    isLast: boolean
    parentSectionGroupReference: SectionGroupNode | null
}

export function buildSectionTree(formItems: FormItem[]): SectionGroupNode[] {
    let sectionGroups: SectionGroupNode[] = [];
    const deletableSectionIDs: string[] = [];
    const maxSectionGroups: Record<string, number> = {};

    const items = [...formItems].sort((a, b) => {
        const sga = a.SectionGroup ?? 0;
        const sgb = b.SectionGroup ?? 0;
        const doa = a.DisplayOrder ?? 0;
        const dob = b.DisplayOrder ?? 0;
        if (sga !== sgb) return sga - sgb;
        return doa - dob;
    });

    for (const item of items.filter((x) => !x.ParentSectionID)) {
        const sectionID = item.SectionID ?? null;
        const itemSectionGroup = item.SectionGroup ?? 0;
        maxSectionGroups[sectionID ?? ''] = itemSectionGroup;

        if (itemSectionGroup > 0 && !deletableSectionIDs.includes(`${sectionID}_${item.ParentSectionGroup ?? 0}`)) {
            deletableSectionIDs.push(`${sectionID}_${item.ParentSectionGroup ?? 0}`);
        }

        let group = sectionGroups.find((x) => x.sectionID === sectionID && x.sectionGroup === itemSectionGroup);
        if (group) {
            group.formItems.push(item);
        } else {
            group = {
                sectionID, parentSectionID: item.ParentSectionID ?? null, heading: item.SectionHeading,
                parentSectionGroup: item.ParentSectionGroup ?? 0, formItems: [item], childSections: [],
                isRepeatable: item.SectionRepeatable, displayOrder: item.SectionDisplayOrder,
                sectionGroup: itemSectionGroup, isDeletable: false, isLast: false, parentSectionGroupReference: null,
            };
            sectionGroups.push(group);
        }
    }

    for (const item of items.filter((x) => !!x.ParentSectionID)) {
        const sectionID = item.SectionID ?? null;
        const parentSectionID = item.ParentSectionID ?? null;
        const parentSectionGroup = item.ParentSectionGroup ?? 0;
        const itemSectionGroup = item.SectionGroup ?? 0;
        maxSectionGroups[`${sectionID}_${parentSectionGroup}`] = itemSectionGroup;

        if (itemSectionGroup > 0 && !deletableSectionIDs.includes(`${sectionID}_${parentSectionGroup}`)) {
            deletableSectionIDs.push(`${sectionID}_${parentSectionGroup}`);
        }

        let group = sectionGroups.find((x) => x.sectionID === sectionID && x.parentSectionGroup === parentSectionGroup && x.sectionGroup === itemSectionGroup);
        if (group) {
            group.formItems.push(item);
        } else {
            const parent = sectionGroups.find((x) => x.sectionID === parentSectionID && x.sectionGroup === parentSectionGroup) ?? null;
            group = {
                sectionID, parentSectionID, heading: item.SectionHeading, parentSectionGroup, formItems: [item],
                childSections: [], isRepeatable: item.SectionRepeatable, displayOrder: item.SectionDisplayOrder,
                sectionGroup: itemSectionGroup, isDeletable: false, isLast: false, parentSectionGroupReference: parent,
            };
            sectionGroups.push(group);
            parent?.childSections.push(group);
        }
    }

    for (const sg of sectionGroups) {
        if (deletableSectionIDs.includes(`${sg.sectionID}_${sg.parentSectionGroup}`)) sg.isDeletable = true;
        const maxSG = maxSectionGroups[sg.sectionID ?? ''];
        const maxSGChild = maxSectionGroups[`${sg.sectionID}_${sg.parentSectionGroup}`];
        if (maxSG === sg.sectionGroup || maxSGChild === sg.sectionGroup) sg.isLast = true;
    }

    sectionGroups = sectionGroups.filter((x) => !x.parentSectionID).sort((a, b) => {
        const doa = a.displayOrder ?? 0;
        const dob = b.displayOrder ?? 0;
        if (doa !== dob) return doa - dob;
        return (a.sectionGroup ?? 0) - (b.sectionGroup ?? 0);
    });

    sectionGroups.forEach((sg) => {
        sg.childSections = sg.childSections.sort((a, b) => {
            const doa = a.displayOrder ?? 0;
            const dob = b.displayOrder ?? 0;
            if (doa !== dob) return doa - dob;
            return (a.sectionGroup ?? 0) - (b.sectionGroup ?? 0);
        });
    });

    return sectionGroups;
}

export function addSectionGroupInstance(sectionGroup: SectionGroupNode): FormItem[] {
    const defaultSectionGroup = sectionGroup.sectionGroup ? sectionGroup.sectionGroup + 1 : 1;
    const newItems: FormItem[] = [];

    sectionGroup.formItems.forEach((original) => {
        newItems.push(getBlankFormItemFromOtherItem(original, defaultSectionGroup, original.ParentSectionGroup ?? 0));
    });

    (sectionGroup.childSections ?? [])
        .filter((cs) => cs.sectionGroup === 0)
        .forEach((childSection) => {
            childSection.formItems.forEach((original) => {
                newItems.push(getBlankFormItemFromOtherItem(original, 0, defaultSectionGroup));
            });
        });

    return newItems;
}

export function removeSectionGroupInstance(formItemsMeta: FormItem[], sectionsToRender: SectionGroupNode[], sectionGroup: SectionGroupNode): FormItem[] {
    let result = [...formItemsMeta];

    const isParent = sectionGroup.childSections && sectionGroup.childSections.length > 0;
    const idsToDelete = sectionGroup.formItems.map((x) => x.ID);
    if (isParent) {
        sectionGroup.childSections.forEach((cs) => idsToDelete.push(...cs.formItems.map((x) => x.ID)));
    }
    result = result.filter((x) => !idsToDelete.includes(x.ID));

    const isChild = !sectionsToRender.find((x) => x.sectionID === sectionGroup.sectionID);
    const nextSectionGroups = isChild
        ? (sectionGroup.parentSectionGroupReference?.childSections ?? []).filter((x) => x.sectionID === sectionGroup.sectionID && x.sectionGroup > sectionGroup.sectionGroup)
        : sectionsToRender.filter((x) => x.sectionID === sectionGroup.sectionID && x.sectionGroup > sectionGroup.sectionGroup);

    nextSectionGroups.forEach((sg) => {
        sg.formItems.forEach((original) => {
            const idx = result.findIndex((x) => x.ID === original.ID);
            if (idx > -1) result[idx] = { ...result[idx], SectionGroup: (result[idx].SectionGroup ?? 0) - 1 };
        });
        if (sg.childSections && sg.childSections.length > 0) {
            sg.childSections.forEach((cs) => {
                cs.formItems.forEach((original) => {
                    const idx = result.findIndex((x) => x.ID === original.ID);
                    if (idx > -1) result[idx] = { ...result[idx], ParentSectionGroup: (result[idx].ParentSectionGroup ?? 0) - 1 };
                });
            });
        }
    });

    return result;
}

export type SectionStatus = 'empty' | 'in-progress' | 'complete' | 'has-error';

function isEmptyResult(value: string | null | undefined): boolean {
    return value === null || value === undefined || value.trim() === '';
}

/**
 * `sectionStats`/`sectionStatus` are recomputed on every render (see the comment above
 * `statusesByKey` in `ManageFormModal.tsx`), while the item's persisted `DataResult` is only
 * written back at hydration/draft-restore/add-remove-instance time — typing goes through
 * `@mantine/form`'s uncontrolled store and never touches `formItemsMeta`. So a live `values` map
 * (`form.getValues()`) is preferred over `item.DataResult` whenever the item's key is present in
 * it — encoded through `encodeFormItemValue` so it lands in the same string shape `DataResult`
 * uses (and the same Table-JSON parsing below applies to it) — falling back to `DataResult` only
 * for an item genuinely not yet in the form store.
 */
function isSectionFieldAnswered(item: FormItem, values: Record<string, unknown>): boolean {
    const hasLiveValue = Object.prototype.hasOwnProperty.call(values, item.ID);
    const raw = hasLiveValue ? encodeFormItemValue(values[item.ID] as FormFieldValue, item.DataType ?? '') : item.DataResult;
    if (item.DataType === 'Table') {
        let parsed: { Rows?: { Columns?: { Value?: string | null }[] }[] };
        try {
            parsed = JSON.parse(raw ?? '{}');
        } catch {
            return false;
        }
        return (parsed.Rows ?? []).some((row) => (row.Columns ?? []).some((col) => !isEmptyResult(col.Value)));
    }
    // A Checkbox's "unanswered" state is the string "false" (defaultDataResultForType), which
    // isEmptyResult wouldn't otherwise recognise as empty — it's a real, non-blank string. Without
    // this, every freshly-added Checkbox (default: unchecked) reads as answered before the user
    // ever touches it, which throws off progress badges and — much more visibly — makes removing a
    // still-untouched repeatable-section instance prompt for confirmation it doesn't need.
    if (item.DataType === 'Checkbox') return raw === 'true';
    return !isEmptyResult(raw);
}

/**
 * Counts how many of a section's own items (not its child sections') have a value, for the
 * table-of-contents / accordion-header "X/Y filled" badge. Informational items carry no user
 * input, so they are excluded from both the numerator and denominator.
 *
 * `values` is the live form values map (`form.getValues()`); see `isSectionFieldAnswered` for why
 * it takes precedence over `item.DataResult`.
 */
export function sectionStats(items: FormItem[], values: Record<string, unknown>): { total: number; answered: number } {
    const relevant = items.filter((item) => item.DataType !== 'InformationalText' && item.DataType !== 'InformationalImage');
    const answered = relevant.filter((item) => isSectionFieldAnswered(item, values)).length;
    return { total: relevant.length, answered };
}

/**
 * Whether removing this section-group instance would discard any answered data. Checks the same
 * item set `removeSectionGroupInstance` itself deletes — the instance's own items plus its
 * immediate child sections' items — so an instance with no answers anywhere in it can be removed
 * without a confirmation prompt.
 */
export function isSectionGroupAnswered(sectionGroup: SectionGroupNode, values: Record<string, unknown>): boolean {
    const items = [...sectionGroup.formItems, ...(sectionGroup.childSections ?? []).flatMap((cs) => cs.formItems)];
    return sectionStats(items, values).answered > 0;
}

/**
 * Mirrors `form-validate.ts`'s `collectTableCellErrors` closely enough to reconstruct the set of
 * synthetic cell-error keys a Table item *could* produce, so `sectionHasError` can check whether
 * any of them are actually present in `form.errors` — table cell errors are keyed by
 * `getTableCellID(...)`, not by the table item's own `FormItem.ID`, so a plain `errors[item.ID]`
 * lookup (which works for every other DataType) would silently miss them. Kept as a small,
 * independent copy rather than sharing the validator's private helper: this only needs to
 * enumerate *possible* cell keys (the emptiness check already lives in `errors`), and duplicating
 * ~15 lines here avoids coupling the status-dot display to the validator's internals.
 */
function tableCellErrorKeys(item: FormItem, formDefinitionFields: FormDefinitionField[], values: Record<string, unknown>): string[] {
    const field = formDefinitionFields.find((f) => f.ID === item.FormDefinitionFieldID);
    if (!field) return [];

    let design: FormTableDesign;
    try {
        design = JSON.parse(field.DataOption ?? '{}');
    } catch {
        return [];
    }
    const columns = design.ColumnDefinitions ?? [];
    if (columns.length === 0) return [];

    const rawValue = values[item.ID];
    const raw = typeof rawValue === 'string' ? rawValue : item.DataResult;
    let parsed: FormTableValue;
    try {
        parsed = JSON.parse(raw ?? '{}');
    } catch {
        parsed = { Rows: [] };
    }
    const storedRows = parsed.Rows ?? [];
    const rows = design.LabelRows
        ? (design.RowDefinitions ?? []).map((rowDef) => storedRows.find((r) => r.RowName === rowDef.Name) ?? { RowName: rowDef.Name, Columns: [] })
        : (storedRows.length > 0 ? storedRows : [{ RowName: null, Columns: [] }]);

    const keys: string[] = [];
    columns.forEach((col) => {
        if (!col.Required || col.DataType === 'Checkbox') return;
        rows.forEach((row, rowIndex) => {
            keys.push(getTableCellID(col.Name, row.RowName ?? '', design.LabelRows ? '' : rowIndex, item.SectionGroup ?? 0));
        });
    });
    return keys;
}

export function sectionHasError(items: FormItem[], errors: FormErrors, formDefinitionFields: FormDefinitionField[], values: Record<string, unknown>): boolean {
    return items.some((item) => {
        if (errors[item.ID]) return true;
        if (item.DataType !== 'Table') return false;
        return tableCellErrorKeys(item, formDefinitionFields, values).some((key) => key in errors);
    });
}

export function sectionStatus(items: FormItem[], errors: FormErrors, formDefinitionFields: FormDefinitionField[], values: Record<string, unknown>): SectionStatus {
    if (sectionHasError(items, errors, formDefinitionFields, values)) return 'has-error';
    const { total, answered } = sectionStats(items, values);
    if (total === 0 || answered === 0) return 'empty';
    if (answered === total) return 'complete';
    return 'in-progress';
}

/**
 * Stable identity for one top-level section-of-the-form entry (one repeatable-section *instance*,
 * or one non-repeatable section) — matches the `key={...}` `FormSection`/`ManageFormModal` already
 * render with, so the table-of-contents/accordion stay in lockstep with the rendered sections.
 */
export function getSectionNavKey(sectionGroup: Pick<SectionGroupNode, 'sectionID' | 'sectionGroup'>): string {
    return `${sectionGroup.sectionID}_${sectionGroup.sectionGroup}`;
}

/**
 * A repeatable section's instances all share the same `heading` (e.g. three "Room Inspected"
 * groups), so — since each instance gets its own table-of-contents entry and accordion card
 * rather than being grouped under one — they are numbered by position among their siblings to
 * stay distinguishable. A section with no siblings (the common case) keeps its plain heading.
 */
export function getSectionNavLabel(sectionGroup: SectionGroupNode, allTopLevelSections: SectionGroupNode[]): string {
    const heading = sectionGroup.heading ?? '';
    const siblings = allTopLevelSections.filter((s) => s.sectionID === sectionGroup.sectionID);
    if (siblings.length <= 1) return heading;
    const index = siblings.indexOf(sectionGroup);
    return `${heading} ${index + 1}`;
}

function collectDescendantItems(sectionGroup: SectionGroupNode): FormItem[] {
    return [...sectionGroup.formItems, ...(sectionGroup.childSections ?? []).flatMap((cs) => collectDescendantItems(cs))];
}

/**
 * Resolves a `form.errors` key — either a plain `FormItem.ID` or a Table cell key from
 * `getTableCellID` — back to the *top-level* `SectionGroupNode` (by `getSectionNavKey`) that owns
 * it, so a failed `form.validate()` can open the right accordion section (Mantine's `Collapse`
 * keeps a closed `Accordion.Panel`'s children mounted but hidden, so `scrollToFirstError` alone
 * would silently scroll to an invisible field) before scrolling to the offending field/cell.
 *
 * Items in a nested `childSections` entry still belong to their top-level ancestor for this
 * purpose — `collectDescendantItems` walks the whole subtree, not just `sectionGroup.formItems`.
 */
export function findSectionKeyForError(
    errorKey: string,
    sectionsToRender: SectionGroupNode[],
    formDefinitionFields: FormDefinitionField[],
    values: Record<string, unknown>,
): string | null {
    for (const sg of sectionsToRender) {
        const items = collectDescendantItems(sg);
        if (items.some((item) => item.ID === errorKey)) return getSectionNavKey(sg);
        const tableMatch = items
            .filter((item) => item.DataType === 'Table')
            .some((item) => tableCellErrorKeys(item, formDefinitionFields, values).includes(errorKey));
        if (tableMatch) return getSectionNavKey(sg);
    }
    return null;
}
